/**
 * Zeiger-Bedienung: Möbel antippen und ziehen, im Rundgang umschauen, messen, Leitungen zeichnen.
 * Hängt direkt am Canvas (Capture-Phase), damit Möbel-Ziehen vor der Kamerasteuerung greift.
 */
import { useEffect } from "react";
import * as THREE from "three";
import { useThree } from "@react-three/fiber";
import { useStore, select, dragTo, endDrag, addMeasurePoint, placement, canDragItems, lineClick, lineClick3d, selectLine, type LineSel } from "../state/store";
import { getHouse } from "./house";
import { FLOORS, floorBase } from "../engine/constants";
import { itemObjects, lineObjects } from "./registry";
import { rig, walk, placeWalkCam } from "./rig";

export function Interaction() {
  const gl = useThree((s) => s.gl);
  const getThree = useThree((s) => s.get);

  useEffect(() => {
    const dom = gl.domElement;
    const ray = new THREE.Raycaster(), ndc = new THREE.Vector2();
    const st = () => useStore.getState();
    const setNdc = (e: PointerEvent) => {
      const r = dom.getBoundingClientRect();
      ndc.set(((e.clientX - r.left) / r.width) * 2 - 1, -((e.clientY - r.top) / r.height) * 2 + 1);
      if (st().mode === "walk") placeWalkCam();
      ray.setFromCamera(ndc, getThree().camera);
    };
    const floorPoint = (z: number) => { const p = new THREE.Vector3(); return ray.ray.intersectPlane(new THREE.Plane(new THREE.Vector3(0, 1, 0), -z), p) ? p : null; };
    /** Treffer auf sichtbarer Wand, Decke, Boden oder Möbel (für Messen und Leitungen in 3D) */
    const shown = (o: THREE.Object3D | null) => { for (; o; o = o.parent) if (!o.visible) return false; return true; };
    const surfaceHit = () => {
      const h = getHouse(st().houseKey || "bestand");
      const objs: THREE.Object3D[] = [...h.floors, h.roof, h.site, ...itemObjects.values()].filter((o) => o.visible);
      for (const hit of ray.intersectObjects(objs, true)) {
        const m = hit.object as THREE.Mesh;
        if (!m.isMesh || !shown(m) || (m.material as THREE.Material).visible === false) continue;
        const n = hit.face ? hit.face.normal.clone().transformDirection(m.matrixWorld) : new THREE.Vector3();
        return { p: hit.point.clone(), n };
      }
      return null;
    };
    const pickItem = (): string | null => {
      const objs: THREE.Object3D[] = [];
      for (const g of itemObjects.values()) if (g.visible && g.children[0]) objs.push(g.children[0]);
      const hit = ray.intersectObjects(objs, true)[0]; if (!hit) return null;
      let o: THREE.Object3D | null = hit.object; while (o && !o.userData.itemId) o = o.parent;
      return o ? (o.userData.itemId as string) : null;
    };
    /** Leitung/Anschluss in Bildschirmnähe (14 px) finden – Rohre sind zu dünn zum direkten Treffen */
    const pickLine = (e: PointerEvent): LineSel => {
      const s = st(), cam = getThree().camera, r = dom.getBoundingClientRect();
      const px = e.clientX - r.left, py = e.clientY - r.top;
      const scr = (x: number, y: number, z: number, f: number) => {
        const v = new THREE.Vector3(x, floorBase(f, x, y) + z, -y).project(cam);
        return v.z > 1 ? null : [((v.x + 1) / 2) * r.width, ((1 - v.y) / 2) * r.height] as const;
      };
      let best: LineSel = null, bd = 14;
      for (const p of s.points) {
        if (!lineObjects.has(p.id)) continue;
        const q = scr(p.x, p.y, p.z, p.f); if (!q) continue;
        const d = Math.hypot(q[0] - px, q[1] - py); if (d < bd + 4) { bd = d - 4; best = { kind: "point", id: p.id }; }
      }
      for (const l of s.lines) {
        if (!lineObjects.has(l.id)) continue;
        for (let i = 1; i < l.pts.length; i++) {
          const a = scr(...l.pts[i - 1], l.f), b = scr(...l.pts[i], l.f); if (!a || !b) continue;
          const vx = b[0] - a[0], vy = b[1] - a[1], L2 = vx * vx + vy * vy || 1;
          const t = Math.max(0, Math.min(1, ((px - a[0]) * vx + (py - a[1]) * vy) / L2));
          const d = Math.hypot(a[0] + t * vx - px, a[1] + t * vy - py);
          if (d < bd) { bd = d; best = { kind: "line", id: l.id }; }
        }
      }
      return best;
    };
    const restoreControls = () => { const m = st().mode; if (rig.orbitP) rig.orbitP.enabled = m === "orbit"; if (rig.orbitO) rig.orbitO.enabled = m === "plan"; };

    let drag: { id: string; ox: number; oy: number; pid: number; moved: boolean } | null = null;
    let down: { x: number; y: number; pid: number } | null = null;
    let look: { pid: number; x: number; y: number } | null = null;

    const onDown = (e: PointerEvent) => {
      setNdc(e); down = { x: e.clientX, y: e.clientY, pid: e.pointerId };
      if (st().measure || st().tool) { // im Rundgang trotzdem umschauen; kurzes Antippen setzt den Punkt
        if (st().mode === "walk") { look = { pid: e.pointerId, x: e.clientX, y: e.clientY }; dom.setPointerCapture(e.pointerId); }
        return;
      }
      // Möbel ziehen nur im Bearbeiten-Modus; sonst wählt erst ein Antippen aus (Kamera dreht frei)
      if (!canDragItems()) { if (st().mode === "walk") { look = { pid: e.pointerId, x: e.clientX, y: e.clientY }; dom.setPointerCapture(e.pointerId); } return; }
      const id = pickItem();
      if (id) {
        select(id);
        const it = st().items.find((i) => i.id === id)!;
        if (it.lock) return; // nur auswählen, Kamera darf weiter drehen
        const p = floorPoint(floorBase(it.f, it.x, it.y));
        if (p) {
          drag = { id, ox: it.x - p.x, oy: it.y + p.z, pid: e.pointerId, moved: false };
          if (rig.orbitP) rig.orbitP.enabled = false; if (rig.orbitO) rig.orbitO.enabled = false;
          dom.setPointerCapture(e.pointerId);
        }
        e.stopPropagation(); return;
      }
      if (st().mode === "walk") { look = { pid: e.pointerId, x: e.clientX, y: e.clientY }; dom.setPointerCapture(e.pointerId); }
    };
    const onMove = (e: PointerEvent) => {
      if (drag && e.pointerId === drag.pid) {
        setNdc(e);
        const s = st(); const it = s.items.find((i) => i.id === drag!.id); if (!it) return;
        const p = floorPoint(floorBase(it.f, it.x, it.y));
        if (p) {
          const [x, y] = placement(s.items).snapPos(it, p.x + drag.ox, -p.z + drag.oy, s.prefs.grid, s.prefs.magnet);
          dragTo(it.id, x, y); drag.moved = true;
        }
        return;
      }
      if (look && e.pointerId === look.pid) {
        walk.yaw -= (e.clientX - look.x) * 0.005;
        walk.pitch = Math.max(-1.2, Math.min(1.2, walk.pitch - (e.clientY - look.y) * 0.004));
        look.x = e.clientX; look.y = e.clientY;
      }
    };
    const onUp = (e: PointerEvent) => {
      const tap = !!down && e.pointerId === down.pid && Math.hypot(e.clientX - down.x, e.clientY - down.y) < 7;
      if (drag && e.pointerId === drag.pid) { if (drag.moved) endDrag(); drag = null; restoreControls(); }
      else if (tap) { setNdc(e); onTap(e); }
      if (look && e.pointerId === look.pid) look = null;
      down = null;
    };
    const onTap = (e: PointerEvent) => {
      const s = st();
      if (s.measure) {
        // 3D: genau auf die angetippte Fläche (Wand oben, Decke …); Grundriss: auf dem Boden
        if (s.mode !== "plan") { const h = surfaceHit(); if (h) { addMeasurePoint([h.p.x, h.p.y, h.p.z]); return; } }
        const f = s.floor === "all" ? 1 : s.floor; const p = floorPoint(FLOORS[f].z); if (p) addMeasurePoint([p.x, p.y, p.z]);
        return;
      }
      if (s.tool === "lines") {
        if (!s.draft.length) { const hit = pickLine(e); if (hit) { selectLine(hit); return; } }
        if (s.mode === "plan") {
          if (s.floor === "all") return;
          const p = floorPoint(FLOORS[s.draft.length ? s.draftF : s.floor].z); if (p) lineClick(p.x, -p.z, e.shiftKey);
          return;
        }
        const h = surfaceHit(); if (!h) return;
        const q = h.p.addScaledVector(h.n, 0.03); // Leitung liegt auf der Wand, nicht darin
        lineClick3d(q.x, -q.z, q.y, e.shiftKey);
        return;
      }
      const hit = pickLine(e); if (hit) { selectLine(hit); return; }
      const id = pickItem(); if (id) { select(id); return; }
      select(null); selectLine(null);
    };

    dom.addEventListener("pointerdown", onDown, { capture: true });
    dom.addEventListener("pointermove", onMove);
    dom.addEventListener("pointerup", onUp);
    dom.addEventListener("pointercancel", onUp);
    return () => {
      dom.removeEventListener("pointerdown", onDown, { capture: true });
      dom.removeEventListener("pointermove", onMove);
      dom.removeEventListener("pointerup", onUp);
      dom.removeEventListener("pointercancel", onUp);
    };
  }, [gl, getThree]);

  return null;
}
